import { ReactNode, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Upload, FileEdit, Lock, ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'
import { usePeriodStore } from '@/stores/periodStore'
import { SpinnerIcon } from '@/components/ui/SpinnerIcon'
import { WorkspaceSidebar, WorkspaceStep } from './WorkspaceSidebar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

export type WizardStepKey = 'upload' | 'adjustments' | 'review'

interface WizardLayoutProps {
  children: ReactNode
  currentStep: WizardStepKey
}

export function WizardLayout({ children, currentStep }: WizardLayoutProps) {
  const { periodId } = useParams<{ periodId: string }>()
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const { periods, loading, error, loadPeriods, getPeriod, setCurrentPeriod } = usePeriodStore()

  // Load periods if not loaded
  useEffect(() => {
    if (currentUser && periods.length === 0 && !loading) {
      loadPeriods(currentUser)
    }
  }, [currentUser, periods.length, loading, loadPeriods])

  // Set current period in store
  useEffect(() => {
    if (periodId) {
      setCurrentPeriod(periodId)
    }
  }, [periodId, setCurrentPeriod])

  const periodData = periodId ? getPeriod(periodId) : undefined

  // Check access control - locked periods redirect to export page
  useEffect(() => {
    if (!periodData || !periodId) return

    const isLocked = periodData.status === 'locked'

    if (isLocked) {
      // Locked periods go to export (outside wizard)
      navigate(`/periods/${periodId}/export`, { replace: true })
    }
  }, [periodData, periodId, navigate])

  const isLocked = periodData?.status === 'locked'

  // Build steps - wizard only has 3 steps (export is outside wizard)
  const workspaceSteps: WorkspaceStep[] = [
    {
      key: 'upload',
      label: 'Ladda upp',
      description: 'Importera filer från Bolt och Uber',
      icon: Upload,
    },
    {
      key: 'adjustments',
      label: 'Justeringar',
      description: 'Granska och justera inkomster',
      icon: FileEdit,
    },
    {
      key: 'review',
      label: 'Granska och Lås',
      description: 'Kontrollera och lås perioden',
      icon: Lock,
    },
  ]

  const handleStepClick = (stepKey: string) => {
    if (periodId) {
      navigate(`/periods/${periodId}/${stepKey}`)
    }
  }

  const handleBackToPeriods = () => {
    navigate('/periods')
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <SpinnerIcon size="lg" className="text-blue-600" />
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-red-600">{error}</p>
      </div>
    )
  }

  // Period not found (might still be loading or doesn't exist)
  if (!periodData && periods.length > 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-red-600">Perioden hittades inte</p>
      </div>
    )
  }

  // Still loading periods
  if (!periodData) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <SpinnerIcon size="lg" className="text-blue-600" />
      </div>
    )
  }

  // Period name for display
  const periodName = periodData.name || periodId || 'Period'

  return (
    <div className="flex flex-1 flex-col md:flex-row overflow-hidden">
      {/* Desktop Sidebar */}
      <div className="hidden md:flex">
        <WorkspaceSidebar
          steps={workspaceSteps}
          currentStepKey={currentStep}
          onStepClick={handleStepClick}
          header={
            <div className="space-y-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToPeriods}
                className="gap-2 -ml-2 text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
                Alla perioder
              </Button>
              <div>
                <h2 className="font-semibold text-lg">{periodName}</h2>
                {isLocked ? (
                  <Badge variant="default" className="bg-green-600 mt-1">Låst</Badge>
                ) : (
                  <Badge variant="secondary" className="mt-1">Utkast</Badge>
                )}
              </div>
            </div>
          }
        />
      </div>

      {/* Mobile header */}
      <div className="md:hidden border-b bg-background px-4 py-3">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackToPeriods}
            className="gap-2 -ml-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Tillbaka
          </Button>
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{periodName}</span>
            {isLocked ? (
              <Badge variant="default" className="bg-green-600">Låst</Badge>
            ) : (
              <Badge variant="secondary">Utkast</Badge>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto bg-muted/20 p-4 md:p-6 pb-20 md:pb-6">
        <div className="max-w-4xl mx-auto">
          {children}
        </div>
      </main>

      {/* Mobile bottom navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 border-t bg-background">
        <div className="flex">
          {workspaceSteps.map((step, index) => {
            const isCurrent = step.key === currentStep
            const Icon = step.icon

            return (
              <button
                key={step.key}
                onClick={() => !step.isDisabled && handleStepClick(step.key)}
                disabled={step.isDisabled}
                className={cn(
                  'flex-1 flex flex-col items-center gap-1 py-2 px-1 text-xs transition-colors',
                  step.isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
                  isCurrent ? 'text-primary bg-primary/5' : 'text-muted-foreground'
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="truncate max-w-full">{index + 1}. {step.label}</span>
              </button>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
